/**
 * Lo ÚNICO que el panel decide por su cuenta sobre una reunión: si el usuario actual puede votar y qué
 * votó. Todo lo demás —avance, resultados, quórum, proyección— llega calculado del backend (D-11;
 * ADR-0015 #6: el desglose lo da la API, el panel no lo estima): ya divergió una vez entre el panel y
 * la API y no se vuelve a derivar aquí.
 */

/** La parte de una reunión que hace falta para decidir sobre el voto del usuario actual. */
export interface VotableMeeting {
  meetingStatus: string
  selectedVoters?: (string | { _id: string })[]
  /** `memberId` falta en los votos heredados (anteriores al ADR-0013), que solo traen `memberName`. */
  participants?: { memberId?: string | { _id: string }; memberName?: string; decision: 'Aprobado' | 'Rechazado'; memberComments?: string | null }[]
}

//! Los ids llegan en dos formas —`ObjectId` serializado como string o un usuario poblado— y a veces
//! como `ObjectId` sin serializar en la pestaña. Se compara SIEMPRE con `String()` a ambos lados
//! (`lease-committee.ts:172,180`): sin eso «ya votaste» y «no estás convocado» salen al revés.
/** El id como string, venga crudo, poblado o vacío. Es la única `idOf` del módulo: no se vuelve a escribir. */
export function idOf(value: string | { _id: string } | null | undefined): string {
  if (!value) return ''

  return String(typeof value === 'object' ? value._id : value)
}

/** El voto que el usuario actual ya emitió en esta reunión, si lo hay. */
export function myVote<T extends VotableMeeting>(meeting: T | null | undefined, userId: string): NonNullable<T['participants']>[number] | undefined {
  if (!meeting || !userId) return undefined

  return meeting.participants?.find((v) => idOf(v.memberId) === String(userId))
}

/**
 * Si el usuario actual puede votar en esta reunión, ahora.
 *
 * Cinco negativos, cada uno con su razón: sin el permiso `committee.vote` el endpoint responde 403;
 * sin estar convocado responde 400; con voto ya emitido se edita, no se vuelve a votar; con la
 * reunión cerrada es inmutable (`assertMeetingOpen`); y con la sesión cerrada la reunión también lo
 * está, aunque un dato viejo diga otra cosa.
 */
export function canVote(meeting: VotableMeeting | null | undefined, sessionStatus: 'open' | 'closed', userId: string, hasPermission: boolean): boolean {
  if (!hasPermission || !meeting || !userId) return false
  if (sessionStatus !== 'open' || meeting.meetingStatus !== 'Open') return false
  if (!(meeting.selectedVoters ?? []).some((v) => idOf(v) === String(userId))) return false

  return !myVote(meeting, userId)
}

/** Nombre completo, o el correo si el usuario no tiene nombres capturados. */
export function fullName(user: { names?: string; lastNames?: string; email?: string } | null | undefined): string {
  if (!user) return ''
  const name = `${user.names ?? ''} ${user.lastNames ?? ''}`.trim()

  return name || user.email || ''
}

/** Las iniciales para el avatar: una o dos letras, nunca más. */
export function initials(name: string): string {
  return (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}
